// Viewport-based fetching of parking places / restrictions. Only what is on
// screen is requested, and repeated identical requests are skipped.

import { api } from "./api";

let lastKey = "";
let lastResult = [];
let inflight = null;

const round = (n) => Math.round(n * 1000) / 1000;

export async function fetchParkingPlaces(
  bounds,
  kinds,
  { includeAccessible = false, includePendingCommunity = false } = {}
) {
  if (!kinds.length && !includeAccessible && !includePendingCommunity) return [];
  const params = {
    min_lat: round(bounds.getSouth()),
    min_lng: round(bounds.getWest()),
    max_lat: round(bounds.getNorth()),
    max_lng: round(bounds.getEast()),
    kinds: kinds.join(","),
    ...(includeAccessible ? { include_accessible: true } : {}),
    ...(includePendingCommunity ? { include_pending_community: true } : {}),
  };
  const key = JSON.stringify(params);
  if (key === lastKey) return lastResult;
  if (inflight) inflight.abort();
  const controller = new AbortController();
  inflight = controller;
  try {
    const res = await api.get("/parking-places", { params, signal: controller.signal });
    lastKey = key;
    lastResult = res.data.places || [];
    return lastResult;
  } catch {
    return lastResult;
  } finally {
    if (inflight === controller) inflight = null;
  }
}

export async function fetchNearbyParking({ lat, lng, radius_m, preference, limit }) {
  const { data } = await api.get("/parking-places/nearby", {
    params: { lat, lng, radius_m, preference, ...(limit ? { limit } : {}) },
  });
  return data.options || [];
}

const metresBetween = (a, b) => {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

/* Small "starter set" so the map is never empty at wide zoom levels: the nearest
   free spot, the nearest paid/public one, then the closest remaining options,
   spread apart so they don't stack on top of each other. */
export async function fetchStarterParking({ lat, lng, radius_m, kinds, limit = 6, minGapM = 700 }) {
  const options = await fetchNearbyParking({ lat, lng, radius_m, preference: "any", limit: 30 });
  const allowed = options.filter((o) => kinds.includes(o.kind));
  const picked = [];
  const farEnough = (o) => picked.every((p) => metresBetween(p, o) >= minGapM);
  const take = (kind) => {
    const hit = allowed.find((o) => o.kind === kind && !picked.includes(o) && farEnough(o));
    if (hit) picked.push(hit);
  };
  take("free_parking");
  take("paid_parking");
  for (const o of allowed) {
    if (picked.length >= limit) break;
    if (!picked.includes(o) && farEnough(o)) picked.push(o);
  }
  return picked;
}

/* Screen-space thinning: at most one marker per grid cell of `cellPx`, closest to
   the centre of the view first, capped at `max`. Keeps a wide view readable
   without a second backend call — the same bbox response is reused. */
export function thinBySpacing(places, map, cellPx, max) {
  const size = map.getSize();
  const mid = { x: size.x / 2, y: size.y / 2 };
  const cells = new Map();
  for (const p of places) {
    const pt = map.latLngToContainerPoint([p.lat, p.lng]);
    const key = `${Math.floor(pt.x / cellPx)}:${Math.floor(pt.y / cellPx)}`;
    const d = (pt.x - mid.x) ** 2 + (pt.y - mid.y) ** 2;
    const cur = cells.get(key);
    if (!cur || d < cur.d) cells.set(key, { place: p, d });
  }
  return [...cells.values()].sort((a, b) => a.d - b.d).slice(0, max).map((c) => c.place);
}
